import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import i18n from "../../i18n";
import { useLocation, useNavigate } from "react-router-dom";
import GuidedTour from "./components/GuidedTour";
import HelpDrawer from "./components/HelpDrawer";
import AcademyLauncher from "./components/AcademyLauncher";
import WorkflowTutorial from "./components/WorkflowTutorial";
import WorkflowCleanupDialog from "./components/WorkflowCleanupDialog";
import api from "../../shared/api/apiClient";
import {
  getTenantId,
  getUserId,
} from "../../shared/lib/auth/authHelper";
import { ACADEMY_PAGES, resolveAcademyPage } from "./data/academyContent";
import {
  CRM_FULL_CYCLE_WORKFLOW,
  createWorkflowSample,
  TASK_PLANNING_WORKFLOW,
  createTaskPlanningSample,
  TABLES_WORKFLOW,
  createTablesWorkflowSample,
} from "./data/crmFullCycleWorkflow";
import {
  MARKETING_BIRTHDAY_WORKFLOW,
  MARKETING_GLOBAL_WORKFLOW,
  MARKETING_WORKFLOWS,
} from "./data/marketingWorkflows";
import "./academy.css";
import "./academy_auto_advance.css";

const AcademyContext = createContext(null);

const LEGACY_STORAGE_KEY = "velsoft-academy:v1";
const PROGRESS_SYNC_DEBOUNCE_MS = 700;
const WORKFLOW_PROGRESS_VERSION = 4;
const WORKFLOW_IDLE_POLL_MS = 30000;
const WORKFLOW_EVENT_DEBOUNCE_MS = 250;
const WORKFLOW_RETRY_BACKOFF_MS = [10000, 20000, 40000, 60000];
const CLEANUP_STATUS_IDLE_POLL_MS = 60000;
const CLEANUP_STATUS_SCHEDULED_POLL_MS = 15000;
const CLEANUP_STATUS_RUNNING_POLL_MS = 1500;
const CLEANUP_RUNNING_STORAGE_KEY = "academy-cleanup-running-session";
const MAX_PROGRESS_WRITES_PER_SYNC = 2;

function defaultStoredState() {
  return {
    completed: {},
    dismissed: {},
    autoStart: false,
    workflow: null,
    trainingSession: null,
  };
}

function normalizeStoredState(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    completed:
      source.completed && typeof source.completed === "object"
        ? source.completed
        : {},
    dismissed:
      source.dismissed && typeof source.dismissed === "object"
        ? source.dismissed
        : {},
    autoStart: source.autoStart === true,
    workflow:
      source.workflow && typeof source.workflow === "object"
        ? source.workflow
        : null,
    trainingSession:
      source.trainingSession && typeof source.trainingSession === "object"
        ? source.trainingSession
        : null,
  };
}

function stableSerialize(value) {
  return JSON.stringify(value, (_key, nestedValue) => {
    if (
      !nestedValue ||
      typeof nestedValue !== "object" ||
      Array.isArray(nestedValue)
    ) {
      return nestedValue;
    }

    return Object.keys(nestedValue)
      .sort()
      .reduce((sorted, key) => {
        sorted[key] = nestedValue[key];
        return sorted;
      }, {});
  });
}

function academyStorageKey() {
  const tenantId = getTenantId();
  const userId = getUserId();
  if (tenantId == null || userId == null) return LEGACY_STORAGE_KEY;
  return `${LEGACY_STORAGE_KEY}:tenant:${tenantId}:user:${userId}`;
}

function hasMeaningfulProgress(value) {
  const state = normalizeStoredState(value);
  return Boolean(
    Object.values(state.completed).some(Boolean) ||
      Object.values(state.dismissed).some(Boolean) ||
      state.workflow ||
      state.trainingSession ||
      state.autoStart,
  );
}

function readStoredState() {
  try {
    const scopedKey = academyStorageKey();
    const scopedValue = localStorage.getItem(scopedKey);
    const legacyValue =
      scopedKey !== LEGACY_STORAGE_KEY
        ? localStorage.getItem(LEGACY_STORAGE_KEY)
        : null;
    return normalizeStoredState(
      JSON.parse(scopedValue || legacyValue || "{}"),
    );
  } catch {
    return defaultStoredState();
  }
}

export function AcademyProvider({ children, enabled = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storageKeyRef = useRef(academyStorageKey());
  const [stored, setStored] = useState(readStoredState);
  const storedRef = useRef(stored);
  const [progressHydrated, setProgressHydrated] = useState(false);
  const [progressSyncRetryTick, setProgressSyncRetryTick] = useState(0);
  const [helpPageKey, setHelpPageKey] = useState(null);
  const [tourPageKey, setTourPageKey] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [workflowState, setWorkflowState] = useState(() => stored.workflow);
  const [cleanupDialogMode, setCleanupDialogMode] = useState(() =>
    sessionStorage.getItem(CLEANUP_RUNNING_STORAGE_KEY) ? "deleting" : null,
  );
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [taskPlanningStarting, setTaskPlanningStarting] = useState(false);
  const [taskPlanningStartError, setTaskPlanningStartError] = useState("");
  const [workflowActionLoading, setWorkflowActionLoading] = useState(false);
  const [workflowActionError, setWorkflowActionError] = useState("");
  const taskPlanningStartBusyRef = useRef(false);
  const taskPlanningFormNavigationRef = useRef("");
  const workflowSyncBusyRef = useRef(false);
  const workflowSyncQueuedRef = useRef(false);
  const progressRevisionRef = useRef(0);
  const progressSyncTimerRef = useRef(null);
  const progressSyncBusyRef = useRef(false);
  const progressSyncQueuedRef = useRef(false);
  const lastServerStateRef = useRef("");
  const workflowSyncIdentity = useMemo(
    () => ({
      key: workflowState?.key,
      contactEmail: workflowState?.sample?.contactEmail,
      marker: workflowState?.sample?.marker,
      leadTitle: workflowState?.sample?.leadTitle,
      leadBudget: workflowState?.sample?.leadBudget,
      leadPrepayment: workflowState?.sample?.leadPrepayment,
      taskTitle: workflowState?.sample?.taskTitle,
      activityTitle: workflowState?.sample?.activityTitle,
    }),
    [
      workflowState?.key,
      workflowState?.sample?.contactEmail,
      workflowState?.sample?.marker,
      workflowState?.sample?.leadTitle,
      workflowState?.sample?.leadBudget,
      workflowState?.sample?.leadPrepayment,
      workflowState?.sample?.taskTitle,
      workflowState?.sample?.activityTitle,
    ],
  );

  const currentPageKey = resolveAcademyPage(location.pathname);
  const currentPage = currentPageKey ? ACADEMY_PAGES[currentPageKey] : null;

  const applyServerProgress = useCallback((payload) => {
    const next = normalizeStoredState(payload?.state);
    progressRevisionRef.current = Number(payload?.revision || 0);
    lastServerStateRef.current = stableSerialize(next);
    storedRef.current = next;
    setStored(next);
    setWorkflowState(next.workflow);
    return next;
  }, []);

  useEffect(() => {
    storedRef.current = stored;
    localStorage.setItem(storageKeyRef.current, JSON.stringify(stored));
  }, [stored]);

  useEffect(() => {
    if (!enabled) {
      setProgressHydrated(false);
      return undefined;
    }

    let active = true;

    const hydrateProgress = async () => {
      try {
        const response = await api.get(
          "/api/system/academy-workflow/progress",
        );
        if (!active) return;

        let payload = response.data || {};
        if (!payload.exists && hasMeaningfulProgress(storedRef.current)) {
          const migrated = await api.put(
            "/api/system/academy-workflow/progress",
            {
              revision: 0,
              state: normalizeStoredState(storedRef.current),
            },
          );
          if (!active) return;
          payload = migrated.data || payload;
        }

        applyServerProgress(payload);
      } catch (error) {
        if (!active) return;

        if (error?.response?.status === 409) {
          try {
            const latest = await api.get(
              "/api/system/academy-workflow/progress",
            );
            if (active) applyServerProgress(latest.data || {});
          } catch {
            // Keep the scoped local cache available while the server recovers.
          }
        }
      } finally {
        if (active) setProgressHydrated(true);
      }
    };

    hydrateProgress();

    return () => {
      active = false;
    };
  }, [applyServerProgress, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const retryPendingProgress = () => {
      setProgressSyncRetryTick((value) => value + 1);
    };

    window.addEventListener("online", retryPendingProgress);
    return () => {
      window.removeEventListener("online", retryPendingProgress);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !progressHydrated) return undefined;

    const serialized = stableSerialize(normalizeStoredState(stored));
    if (serialized === lastServerStateRef.current) return undefined;

    if (progressSyncTimerRef.current != null) {
      window.clearTimeout(progressSyncTimerRef.current);
    }

    progressSyncTimerRef.current = window.setTimeout(async () => {
      progressSyncTimerRef.current = null;
      if (progressSyncBusyRef.current) {
        progressSyncQueuedRef.current = true;
        return;
      }

      progressSyncBusyRef.current = true;
      try {
        let writeAttempts = 0;
        do {
          writeAttempts += 1;
          progressSyncQueuedRef.current = false;
          const currentState = normalizeStoredState(storedRef.current);
          const currentSerialized = stableSerialize(currentState);
          if (currentSerialized === lastServerStateRef.current) break;

          try {
            const response = await api.put(
              "/api/system/academy-workflow/progress",
              {
                revision: progressRevisionRef.current,
                state: currentState,
              },
            );
            progressRevisionRef.current = Number(
              response.data?.revision || 0,
            );
            lastServerStateRef.current = stableSerialize(
              normalizeStoredState(response.data?.state),
            );
          } catch (error) {
            if (error?.response?.status !== 409) break;
            const latest = await api.get(
              "/api/system/academy-workflow/progress",
            );
            applyServerProgress(latest.data || {});
            break;
          }
        } while (
          writeAttempts < MAX_PROGRESS_WRITES_PER_SYNC &&
          (progressSyncQueuedRef.current ||
            stableSerialize(normalizeStoredState(storedRef.current)) !==
              lastServerStateRef.current)
        );
      } catch {
        // The scoped local cache remains usable; reconnect retries the write.
      } finally {
        progressSyncBusyRef.current = false;
      }
    }, PROGRESS_SYNC_DEBOUNCE_MS);

    return () => {
      if (progressSyncTimerRef.current != null) {
        window.clearTimeout(progressSyncTimerRef.current);
        progressSyncTimerRef.current = null;
      }
    };
  }, [
    applyServerProgress,
    enabled,
    progressHydrated,
    progressSyncRetryTick,
    stored,
  ]);

  useEffect(() => {
    setStored((prev) => ({
      ...prev,
      workflow: workflowState,
    }));
  }, [workflowState]);

  useEffect(() => {
    const sample = workflowSyncIdentity;
    if (
      !enabled ||
      ![
        CRM_FULL_CYCLE_WORKFLOW.key,
        TASK_PLANNING_WORKFLOW.key,
        TABLES_WORKFLOW.key,
      ].includes(sample.key) ||
      !sample?.contactEmail ||
      !sample?.marker
    ) {
      return undefined;
    }

    let active = true;
    let syncTimer = null;
    let requestController = null;
    let consecutiveFailures = 0;

    const canSync = () =>
      active &&
      document.visibilityState !== "hidden" &&
      navigator.onLine !== false;

    const clearSyncTimer = () => {
      if (syncTimer != null) {
        window.clearTimeout(syncTimer);
        syncTimer = null;
      }
    };

    const retryAfterMs = (error) => {
      const headerValue = error?.response?.headers?.["retry-after"];
      const responseSeconds = error?.response?.data?.retry_after_seconds;
      const seconds = Number(responseSeconds ?? headerValue);

      if (Number.isFinite(seconds) && seconds > 0) {
        return Math.min(seconds * 1000, 60000);
      }

      if (typeof headerValue === "string") {
        const retryAt = Date.parse(headerValue);
        if (Number.isFinite(retryAt)) {
          return Math.min(Math.max(retryAt - Date.now(), 0), 60000);
        }
      }

      return 0;
    };

    let syncWorkflow;

    const scheduleSync = (delay = WORKFLOW_IDLE_POLL_MS) => {
      clearSyncTimer();
      if (!canSync()) return;

      syncTimer = window.setTimeout(
        () => {
          syncTimer = null;
          syncWorkflow();
        },
        Math.max(0, Number(delay) || 0),
      );
    };

    syncWorkflow = async () => {
      if (!canSync()) return;
      if (workflowSyncBusyRef.current) {
        workflowSyncQueuedRef.current = true;
        scheduleSync(WORKFLOW_EVENT_DEBOUNCE_MS);
        return;
      }

      clearSyncTimer();
      workflowSyncBusyRef.current = true;
      requestController = new AbortController();
      let failed = false;
      let nextDelay = WORKFLOW_IDLE_POLL_MS;

      try {
        const response = await api.get("/api/system/academy-workflow/state", {
          params: {
            contact_email: sample.contactEmail,
            marker: sample.marker,
            lead_title: sample.leadTitle,
            lead_budget: sample.leadBudget,
            lead_prepayment: sample.leadPrepayment,
            task_title: sample.taskTitle,
            activity_title: sample.activityTitle,
          },
          signal: requestController.signal,
        });

        if (!active) return;
        consecutiveFailures = 0;
        const state = response.data || {};

        setWorkflowState((previous) => {
          if (!previous?.sample) return previous;

          const current = previous.sample;
          const contact = state.contact || {};
          const lead = state.lead || {};
          const task = state.task || {};
          const deal = state.deal || {};
          const activity = state.activity || {};
          const hasInitialDealStage = Object.prototype.hasOwnProperty.call(
            current,
            "dealInitialStageId",
          );
          const initialDealStage = hasInitialDealStage
            ? current.dealInitialStageId
            : deal.exists
              ? (deal.stage_id ?? "none")
              : undefined;
          const currentDealStage = deal.exists
            ? (deal.stage_id ?? "none")
            : undefined;

          const nextSample = {
            ...current,
            contactExists: Boolean(contact.exists),
            contactId: contact.id ?? current.contactId ?? null,
            contactUiId: contact.ui_id ?? current.contactUiId ?? null,
            leadExists: Boolean(lead.exists),
            leadId: lead.id ?? current.leadId ?? null,
            leadUiId: lead.ui_id ?? current.leadUiId ?? null,
            leadConfigured: Boolean(state.lead_configured),
            taskExists: Boolean(task.exists),
            taskId: task.id ?? current.taskId ?? null,
            taskUiId: task.ui_id ?? current.taskUiId ?? null,
            dealExists: Boolean(deal.exists),
            dealId: deal.id ?? current.dealId ?? null,
            dealUiId: deal.ui_id ?? current.dealUiId ?? null,
            dealStageId: deal.stage_id ?? null,
            activityExists: Boolean(activity.exists),
            activityId: activity.id ?? current.activityId ?? null,
            activityUiId: activity.ui_id ?? current.activityUiId ?? null,
            activityDone:
              Boolean(activity.exists) && activity.status === "done",
            dealWon: Boolean(state.deal_won),
          };

          if (previous.key === TASK_PLANNING_WORKFLOW.key) {
            const dueAt = String(task.due_at || "");
            const now = new Date();
            const todayKey = [
              now.getFullYear(),
              String(now.getMonth() + 1).padStart(2, "0"),
              String(now.getDate()).padStart(2, "0"),
            ].join("-");
            nextSample.taskDueAtActual = task.due_at || null;
            nextSample.taskMovedToday = Boolean(
              current.taskMovedToday ||
                (task.exists && dueAt.slice(0, 10) === todayKey),
            );
            const dueDate = dueAt ? new Date(dueAt) : null;
            nextSample.taskDone = Boolean(state.task_done);
            nextSample.taskOverdue = Boolean(
              !nextSample.taskDone &&
                (state.task_overdue ||
                  (dueDate &&
                    Number.isFinite(dueDate.getTime()) &&
                    dueDate.getTime() < now.getTime())),
            );

            if (JSON.stringify(nextSample) === JSON.stringify(current)) {
              return previous;
            }

            return {
              ...previous,
              progressVersion: WORKFLOW_PROGRESS_VERSION,
              sample: nextSample,
              syncedAt: new Date().toISOString(),
            };
          }

          if (previous.key === TABLES_WORKFLOW.key) {
            nextSample.taskDone = Boolean(state.task_done);
            nextSample.taskOverdue = Boolean(state.task_overdue);
            nextSample.taskDueAtActual = task.due_at || null;

            if (JSON.stringify(nextSample) === JSON.stringify(current)) {
              return previous;
            }

            return {
              ...previous,
              progressVersion: WORKFLOW_PROGRESS_VERSION,
              sample: nextSample,
              syncedAt: new Date().toISOString(),
            };
          }

          // A workflow started before the Task-first sequence was introduced
          // may still contain an old numeric step index. Never let that stale
          // index place the learner on commercial terms before a real linked
          // Task exists.
          const taskActionStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "open-task-create",
          );
          const taskFormStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "create-task",
          );
          const currentStepIndex = Number(previous.stepIndex || 0);
          let guardedStepIndex =
            Boolean(lead.exists) &&
            !Boolean(task.exists) &&
            taskActionStepIndex >= 0 &&
            taskFormStepIndex >= 0 &&
            currentStepIndex > taskFormStepIndex
              ? taskActionStepIndex
              : currentStepIndex;

          // A successfully created Deal is stronger evidence than any stale
          // Lead-step check. Continue inside the Deal workspace even if an
          // older saved tutorial state still points at commercial terms or
          // the conversion button.
          const convertDealStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "convert-deal",
          );
          const inspectDealStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "inspect-deal-timeline",
          );
          if (
            Boolean(deal.exists) &&
            convertDealStepIndex >= 0 &&
            inspectDealStepIndex >= 0 &&
            guardedStepIndex <= convertDealStepIndex
          ) {
            guardedStepIndex = inspectDealStepIndex;
          }

          if (initialDealStage !== undefined) {
            nextSample.dealInitialStageId = initialDealStage;
            nextSample.dealStageChanged =
              hasInitialDealStage &&
              currentDealStage !== undefined &&
              String(initialDealStage) !== String(currentDealStage);
          }

          // Older builds silently advanced when a required UI target was not
          // found. Reconcile the saved step with facts from the API so a load
          // error can never turn into a false 100% completion.
          const pipelineStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "set-pipeline",
          );
          const activityStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "create-activity",
          );
          const timelineStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "check-timeline",
          );
          const wonStepIndex = CRM_FULL_CYCLE_WORKFLOW.steps.findIndex(
            (step) => step.id === "win-deal",
          );
          const legacyProgress =
            Number(previous.progressVersion || 0) < WORKFLOW_PROGRESS_VERSION;

          if (
            Boolean(deal.exists) &&
            !Boolean(nextSample.dealWon) &&
            pipelineStepIndex >= 0 &&
            guardedStepIndex > pipelineStepIndex &&
            !Boolean(nextSample.dealStageChanged)
          ) {
            guardedStepIndex = pipelineStepIndex;
          } else if (
            Boolean(deal.exists) &&
            !Boolean(nextSample.dealWon) &&
            activityStepIndex >= 0 &&
            guardedStepIndex > activityStepIndex &&
            !Boolean(nextSample.activityDone)
          ) {
            guardedStepIndex = activityStepIndex;
          } else if (
            legacyProgress &&
            Boolean(nextSample.activityDone) &&
            !Boolean(nextSample.dealWon) &&
            timelineStepIndex >= 0 &&
            guardedStepIndex > timelineStepIndex
          ) {
            guardedStepIndex = timelineStepIndex;
          } else if (
            !Boolean(nextSample.dealWon) &&
            wonStepIndex >= 0 &&
            guardedStepIndex > wonStepIndex
          ) {
            guardedStepIndex = wonStepIndex;
          }

          if (
            JSON.stringify(nextSample) === JSON.stringify(current) &&
            guardedStepIndex === currentStepIndex &&
            !legacyProgress
          ) {
            return previous;
          }

          return {
            ...previous,
            stepIndex: guardedStepIndex,
            progressVersion: WORKFLOW_PROGRESS_VERSION,
            sample: nextSample,
            syncedAt: new Date().toISOString(),
          };
        });
      } catch (error) {
        if (
          !active ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }

        failed = true;
        consecutiveFailures = Math.min(
          consecutiveFailures + 1,
          WORKFLOW_RETRY_BACKOFF_MS.length,
        );
        const backoff =
          WORKFLOW_RETRY_BACKOFF_MS[Math.max(0, consecutiveFailures - 1)];
        nextDelay = Math.max(backoff, retryAfterMs(error));

        // The tutorial keeps its DOM-based fallback when the state endpoint
        // is temporarily unavailable. Network failures and 429 responses use
        // capped backoff instead of immediately retrying and exhausting the
        // shared API rate limit.
      } finally {
        requestController = null;
        workflowSyncBusyRef.current = false;
        if (!active || !canSync()) return;

        const queued = workflowSyncQueuedRef.current;
        workflowSyncQueuedRef.current = false;

        scheduleSync(
          failed
            ? nextDelay
            : queued
              ? WORKFLOW_EVENT_DEBOUNCE_MS
              : WORKFLOW_IDLE_POLL_MS,
        );
      }
    };

    const requestImmediateSync = () => {
      if (!active) return;

      if (!canSync()) {
        workflowSyncQueuedRef.current = true;
        return;
      }

      if (workflowSyncBusyRef.current) {
        workflowSyncQueuedRef.current = true;
        return;
      }

      scheduleSync(WORKFLOW_EVENT_DEBOUNCE_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearSyncTimer();
        return;
      }

      requestImmediateSync();
    };

    syncWorkflow();
    window.addEventListener(
      "velsoft:academy-workflow-refresh",
      requestImmediateSync,
    );
    window.addEventListener("online", requestImmediateSync);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      workflowSyncQueuedRef.current = false;
      clearSyncTimer();
      requestController?.abort();
      window.removeEventListener(
        "velsoft:academy-workflow-refresh",
        requestImmediateSync,
      );
      window.removeEventListener("online", requestImmediateSync);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, workflowSyncIdentity]);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    let timer = null;
    let busy = false;
    let requestController = null;

    const canLoad = () =>
      active &&
      document.visibilityState !== "hidden" &&
      navigator.onLine !== false;

    const clearTimer = () => {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const scheduleLoad = (delay = CLEANUP_STATUS_IDLE_POLL_MS) => {
      clearTimer();
      if (!canLoad()) return;

      timer = window.setTimeout(() => {
        timer = null;
        loadCleanupStatus();
      }, delay);
    };

    const loadCleanupStatus = async () => {
      if (!canLoad() || busy) return;

      clearTimer();
      busy = true;
      requestController = new AbortController();
      let nextDelay = CLEANUP_STATUS_IDLE_POLL_MS;

      try {
        const response = await api.get(
          "/api/system/academy-workflow/cleanup/status",
          { signal: requestController.signal },
        );
        if (!active) return;

        setCleanupStatus(response.data);

        if (response.data?.status === "scheduled") {
          nextDelay = CLEANUP_STATUS_SCHEDULED_POLL_MS;
          setCleanupDialogMode("countdown");
        } else if (response.data?.status === "cleaned") {
          sessionStorage.removeItem(CLEANUP_RUNNING_STORAGE_KEY);
          const alreadyShown =
            sessionStorage.getItem("academy-cleanup-shown-session") ===
            response.data?.session_id;
          const currentSessionId =
            storedRef.current?.trainingSession?.sample?.sessionId;
          const belongsToOlderSession =
            Boolean(currentSessionId) &&
            Boolean(response.data?.session_id) &&
            currentSessionId !== response.data.session_id;

          if (
            !alreadyShown &&
            !belongsToOlderSession &&
            response.data?.session_id
          ) {
            sessionStorage.setItem(
              "academy-cleanup-shown-session",
              response.data.session_id,
            );
            setCleanupDialogMode("cleaned");
          } else if (belongsToOlderSession && response.data?.session_id) {
            sessionStorage.setItem(
              "academy-cleanup-shown-session",
              response.data.session_id,
            );
            setCleanupDialogMode(null);
          }
        }
      } catch {
        // Academy must not block CRM when the cleanup endpoint is unavailable.
      } finally {
        requestController = null;
        busy = false;
        if (sessionStorage.getItem(CLEANUP_RUNNING_STORAGE_KEY)) {
          nextDelay = CLEANUP_STATUS_RUNNING_POLL_MS;
        }
        scheduleLoad(nextDelay);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearTimer();
        return;
      }

      loadCleanupStatus();
    };

    loadCleanupStatus();
    window.addEventListener("online", loadCleanupStatus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      clearTimer();
      requestController?.abort();
      window.removeEventListener("online", loadCleanupStatus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled]);

  useEffect(() => {
    setHelpPageKey(null);
    setTourPageKey(null);
    setTourStep(0);
  }, [location.pathname]);

  useEffect(() => {
    if (enabled) return;

    setHelpPageKey(null);
    setTourPageKey(null);
    setTourStep(0);
    setCleanupDialogMode(null);
    setCleanupError("");
    setWorkflowActionError("");
  }, [enabled]);

  useEffect(() => {
    if (
      !enabled ||
      !stored.autoStart ||
      !currentPageKey ||
      stored.completed[currentPageKey] ||
      stored.dismissed[currentPageKey] ||
      !ACADEMY_PAGES[currentPageKey]?.steps?.length
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setTourPageKey(currentPageKey);
      setTourStep(0);
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [
    enabled,
    currentPageKey,
    stored.autoStart,
    stored.completed,
    stored.dismissed,
  ]);

  const openHelp = useCallback(
    (pageKey = currentPageKey) => {
      if (!pageKey || !ACADEMY_PAGES[pageKey]) return;
      setHelpPageKey(pageKey);
    },
    [currentPageKey],
  );

  const closeHelp = useCallback(() => setHelpPageKey(null), []);

  const startTour = useCallback(
    (pageKey = currentPageKey) => {
      const page = ACADEMY_PAGES[pageKey];
      if (!page) return;

      if (location.pathname !== page.route && page.route) {
        navigate(page.route);
        window.setTimeout(() => {
          setTourPageKey(pageKey);
          setTourStep(0);
        }, 450);
        return;
      }

      if (!page.steps?.length) {
        setHelpPageKey(pageKey);
        return;
      }

      setHelpPageKey(null);
      setTourPageKey(pageKey);
      setTourStep(0);
    },
    [currentPageKey, location.pathname, navigate],
  );

  const closeTour = useCallback(
    (dismiss = false) => {
      if (dismiss && tourPageKey) {
        setStored((prev) => ({
          ...prev,
          dismissed: {
            ...prev.dismissed,
            [tourPageKey]: true,
          },
        }));
      }
      setTourPageKey(null);
      setTourStep(0);
    },
    [tourPageKey],
  );

  const completeTour = useCallback(() => {
    if (tourPageKey) {
      setStored((prev) => ({
        ...prev,
        completed: {
          ...prev.completed,
          [tourPageKey]: true,
        },
        dismissed: {
          ...prev.dismissed,
          [tourPageKey]: false,
        },
      }));
    }
    setTourPageKey(null);
    setTourStep(0);
  }, [tourPageKey]);

  const markLessonComplete = useCallback((pageKey, value = true) => {
    setStored((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        [pageKey]: value,
      },
    }));
  }, []);

  const resetProgress = useCallback(async () => {
    if (progressSyncTimerRef.current != null) {
      window.clearTimeout(progressSyncTimerRef.current);
      progressSyncTimerRef.current = null;
    }

    const response = await api.post(
      "/api/system/academy-workflow/progress/reset",
    );
    applyServerProgress(response.data || {});

    setWorkflowState(null);
    setHelpPageKey(null);
    setTourPageKey(null);
    setTourStep(0);
    setCleanupDialogMode(null);

  }, [applyServerProgress]);

  const setAutoStart = useCallback((value) => {
    setStored((prev) => ({
      ...prev,
      autoStart: Boolean(value),
    }));
  }, []);

  const startFullCycleWorkflow = useCallback(() => {
    const sample = createWorkflowSample();
    const startedAt = new Date().toISOString();
    const nextState = {
      key: CRM_FULL_CYCLE_WORKFLOW.key,
      progressVersion: WORKFLOW_PROGRESS_VERSION,
      stepIndex: 0,
      sample,
      startedAt,
    };

    setStored((prev) => ({
      ...prev,
      trainingSession: { sample, startedAt },
    }));
    setWorkflowState(nextState);
    navigate(CRM_FULL_CYCLE_WORKFLOW.steps[0].route || "/menu/sales/contacts");
  }, [navigate]);

  const startTaskPlanningWorkflow = useCallback(
    async (previousSample = null) => {
      if (taskPlanningStartBusyRef.current) return;
      taskPlanningStartBusyRef.current = true;

      const sourceSample = previousSample?.nativeEvent
        ? stored.trainingSession?.sample
        : previousSample || stored.trainingSession?.sample;
      const sample = createTaskPlanningSample(sourceSample);
      const startedAt =
        sourceSample?.academyStartedAt ||
        stored.trainingSession?.startedAt ||
        new Date().toISOString();
      setTaskPlanningStarting(true);
      setTaskPlanningStartError("");

      try {
        const response = await api.post(
          "/api/system/academy-workflow/task-planning/prepare",
          {
            session_id: sample.sessionId,
            marker: sample.marker,
            contact_id: sample.contactId || null,
            contact_name: sample.contactName,
            contact_email: sample.contactEmail,
            lead_title: sample.leadTitle,
            started_at: startedAt,
          },
        );
        const fixture = response.data || {};
        const nextSample = {
          ...sample,
          contactExists: true,
          contactId: fixture.contact_id,
          contactUiId: fixture.contact_ui_id,
          leadExists: true,
          leadId: fixture.lead_id,
          leadUiId: fixture.lead_ui_id,
        };

        taskPlanningFormNavigationRef.current = "";
        setWorkflowState({
          key: TASK_PLANNING_WORKFLOW.key,
          progressVersion: WORKFLOW_PROGRESS_VERSION,
          stepIndex: 0,
          sample: nextSample,
          startedAt,
        });
        setStored((prev) => ({
          ...prev,
          trainingSession: { sample: nextSample, startedAt },
        }));
        navigate(
          "/menu/sales/contacts/" +
            fixture.contact_id +
            "/personal-details?academy_tab=works&academy_task_planning=1",
        );
      } catch (error) {
        setTaskPlanningStartError(
          error?.response?.data?.detail ||
            i18n.t("academy_next_stage_prepare_failed"),
        );
      } finally {
        taskPlanningStartBusyRef.current = false;
        setTaskPlanningStarting(false);
      }
    },
    [navigate, stored.trainingSession],
  );

  const startTablesWorkflow = useCallback(() => {
    const source = stored.trainingSession;
    if (!source?.sample?.contactId || !source?.sample?.contactEmail) {
      setTaskPlanningStartError(
        i18n.t("academy_complete_main_cycle_first"),
      );
      return;
    }

    const sample = createTablesWorkflowSample(source.sample);
    const startedAt = source.startedAt || new Date().toISOString();
    setWorkflowActionError("");
    setStored((prev) => ({
      ...prev,
      trainingSession: { sample, startedAt },
    }));
    setWorkflowState({
      key: TABLES_WORKFLOW.key,
      progressVersion: WORKFLOW_PROGRESS_VERSION,
      stepIndex: 0,
      sample,
      startedAt,
    });
    navigate("/menu/sales/contacts?academy_tables=1");
  }, [navigate, stored.trainingSession]);

  const startMarketingWorkflow = useCallback(
    (workflowKey) => {
      const workflow = MARKETING_WORKFLOWS[workflowKey];
      if (!workflow) return;

      setWorkflowActionError("");
      setWorkflowState({
        key: workflow.key,
        progressVersion: WORKFLOW_PROGRESS_VERSION,
        stepIndex: 0,
        startedAt: new Date().toISOString(),
      });
      navigate(workflow.steps[0]?.route || "/menu/marketing");
    },
    [navigate],
  );

  useEffect(() => {
    if (
      !enabled ||
      workflowState?.key !== TASK_PLANNING_WORKFLOW.key ||
      Number(workflowState?.stepIndex || 0) !== 1 ||
      workflowState?.sample?.taskExists ||
      !workflowState?.sample?.contactId ||
      !workflowState?.sample?.leadId
    ) {
      return;
    }

    const sample = workflowState.sample;
    const navigationKey = sample.sessionId + ":" + sample.leadId;
    const alreadyOpened =
      location.pathname === "/menu/sales/tasks" &&
      location.state?.academy_task_planning_session === navigationKey;

    if (
      alreadyOpened ||
      taskPlanningFormNavigationRef.current === navigationKey
    ) {
      return;
    }

    taskPlanningFormNavigationRef.current = navigationKey;
    navigate("/menu/sales/tasks", {
      state: {
        contact_ids: [sample.contactId],
        contact_ui_ids: [sample.contactUiId],
        lead_id: sample.leadId,
        lead_ui_id: sample.leadUiId,
        default_title: sample.taskTitle,
        default_description: sample.taskDescription,
        default_due_at: sample.taskDueAt,
        academy_task_planning_session: navigationKey,
        academy_return_to:
          "/menu/sales/contacts/" +
          sample.contactId +
          "/personal-details?academy_tab=works",
      },
    });
  }, [
    enabled,
    location.pathname,
    location.state,
    navigate,
    workflowState?.key,
    workflowState?.sample,
    workflowState?.stepIndex,
  ]);

  const setWorkflowStep = useCallback((stepIndex) => {
    setWorkflowState((prev) => {
      if (!prev) return prev;
      const workflow = {
        [CRM_FULL_CYCLE_WORKFLOW.key]: CRM_FULL_CYCLE_WORKFLOW,
        [TASK_PLANNING_WORKFLOW.key]: TASK_PLANNING_WORKFLOW,
        [TABLES_WORKFLOW.key]: TABLES_WORKFLOW,
        ...MARKETING_WORKFLOWS,
      }[prev.key];
      if (!workflow) return prev;
      return {
        ...prev,
        stepIndex: Math.max(
          0,
          Math.min(Number(stepIndex || 0), workflow.steps.length - 1),
        ),
      };
    });
  }, []);

  const closeWorkflow = useCallback(() => {
    setWorkflowState(null);
  }, []);

  const completeWorkflow = useCallback(() => {
    const workflowKey = workflowState?.key || CRM_FULL_CYCLE_WORKFLOW.key;
    setStored((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        [workflowKey]: true,
      },
      trainingSession: workflowState?.sample
        ? {
            sample: workflowState.sample,
            startedAt:
              workflowState.startedAt ||
              prev.trainingSession?.startedAt ||
              new Date().toISOString(),
          }
        : prev.trainingSession,
    }));
    setCleanupError("");
    setCleanupDialogMode(null);
    setWorkflowState(null);
    navigate("/menu/academy");
  }, [navigate, workflowState]);

  const cleanupPayload = useCallback(() => {
    const source = workflowState?.sample
      ? {
          sample: workflowState.sample,
          startedAt: workflowState.startedAt,
        }
      : stored.trainingSession;
    if (!source?.sample) return null;

    return {
      session_id: source.sample.sessionId,
      marker: source.sample.cleanupMarker || source.sample.marker,
      contact_email: source.sample.contactEmail,
      started_at: source.startedAt || new Date().toISOString(),
    };
  }, [stored.trainingSession, workflowState]);

  const handleWorkflowAction = useCallback(
    async (actionKey) => {
      if (actionKey !== "markTaskOverdue" || workflowActionLoading) return;
      const sample = workflowState?.sample;
      if (!sample?.taskId) {
        setWorkflowActionError(
          i18n.t("academy_training_task_not_found"),
        );
        return;
      }

      setWorkflowActionLoading(true);
      setWorkflowActionError("");
      try {
        const response = await api.post(
          "/api/system/academy-workflow/task-planning/mark-overdue",
          {
            task_id: sample.taskId,
            marker: sample.marker,
            contact_email: sample.contactEmail,
            task_title: sample.taskTitle,
          },
        );
        setWorkflowState((prev) =>
          prev
            ? {
                ...prev,
                sample: {
                  ...prev.sample,
                  taskOverdue: true,
                  taskDone: false,
                  taskDueAtActual:
                    response.data?.due_at || prev.sample.taskDueAtActual,
                },
              }
            : prev,
        );
        window.dispatchEvent(new Event("velsoft:tasks-changed"));
        window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
      } catch (error) {
        setWorkflowActionError(
          error?.response?.data?.detail ||
            i18n.t("academy_overdue_prepare_failed"),
        );
      } finally {
        setWorkflowActionLoading(false);
      }
    },
    [workflowActionLoading, workflowState],
  );

  const openFinalCleanup = useCallback(async () => {
    const payload = cleanupPayload();
    if (!payload) return;

    setCleanupLoading(true);
    setCleanupError("");
    try {
      const response = await api.post(
        "/api/system/academy-workflow/cleanup/preview",
        payload,
      );
      setCleanupStatus(response.data);
      setCleanupDialogMode("confirm");
    } catch (error) {
      setCleanupError(
        error?.response?.data?.detail ||
          i18n.t("academy_cleanup_preview_failed"),
      );
      setCleanupDialogMode("confirm");
    } finally {
      setCleanupLoading(false);
    }
  }, [cleanupPayload]);

  const keepWorkflowData = useCallback(() => {
    setCleanupError("");
    setCleanupDialogMode(null);
    setWorkflowState(null);
    navigate("/menu/academy");
  }, [navigate]);

  const deleteWorkflowDataNow = useCallback(async () => {
    const payload = cleanupPayload();
    if (!payload) return;

    sessionStorage.setItem(
      CLEANUP_RUNNING_STORAGE_KEY,
      JSON.stringify({
        sessionId: payload.session_id,
        startedAt: new Date().toISOString(),
      }),
    );
    setCleanupDialogMode("deleting");
    setCleanupLoading(true);
    setCleanupError("");

    try {
      const response = await api.post(
        "/api/system/academy-workflow/cleanup/now",
        payload,
      );
      sessionStorage.removeItem(CLEANUP_RUNNING_STORAGE_KEY);
      if (response.data?.session_id) {
        sessionStorage.setItem(
          "academy-cleanup-shown-session",
          response.data.session_id,
        );
      }
      setCleanupStatus(response.data);
      setCleanupDialogMode("cleaned");
      setWorkflowState(null);
      setStored((prev) => ({
        ...prev,
        trainingSession: null,
      }));
    } catch (error) {
      sessionStorage.removeItem(CLEANUP_RUNNING_STORAGE_KEY);
      setCleanupError(
        error?.response?.data?.detail || i18n.t("academy_cleanup_failed"),
      );
      setCleanupDialogMode("confirm");
    } finally {
      setCleanupLoading(false);
    }
  }, [cleanupPayload]);

  const closeCleanupNotice = useCallback(() => {
    setCleanupDialogMode(null);
    navigate("/menu/academy");
  }, [navigate]);

  const value = useMemo(
    () => ({
      academyEnabled: enabled,
      progressHydrated,
      currentPageKey,
      currentPage,
      completed: stored.completed,
      autoStart: stored.autoStart,
      openHelp,
      closeHelp,
      startTour,
      closeTour,
      completeTour,
      markLessonComplete,
      resetProgress,
      setAutoStart,
      workflowState,
      trainingSession: stored.trainingSession,
      startFullCycleWorkflow,
      startTaskPlanningWorkflow,
      startTablesWorkflow,
      startMarketingWorkflow,
      taskPlanningStarting,
      taskPlanningStartError,
      workflowActionLoading,
      workflowActionError,
      setWorkflowStep,
      closeWorkflow,
      completeWorkflow,
      openFinalCleanup,
      deleteWorkflowDataNow,
      keepWorkflowData,
      cleanupStatus,
    }),
    [
      enabled,
      progressHydrated,
      currentPageKey,
      currentPage,
      stored.completed,
      stored.autoStart,
      openHelp,
      closeHelp,
      startTour,
      closeTour,
      completeTour,
      markLessonComplete,
      resetProgress,
      setAutoStart,
      workflowState,
      stored.trainingSession,
      startFullCycleWorkflow,
      startTaskPlanningWorkflow,
      startTablesWorkflow,
      startMarketingWorkflow,
      taskPlanningStarting,
      taskPlanningStartError,
      workflowActionLoading,
      workflowActionError,
      setWorkflowStep,
      closeWorkflow,
      completeWorkflow,
      openFinalCleanup,
      deleteWorkflowDataNow,
      keepWorkflowData,
      cleanupStatus,
    ],
  );

  const tourPage = tourPageKey ? ACADEMY_PAGES[tourPageKey] : null;
  const helpPage = helpPageKey ? ACADEMY_PAGES[helpPageKey] : null;

  return (
    <AcademyContext.Provider value={value}>
      {children}

      {enabled && (
        <>
          {currentPage && location.pathname !== "/menu/academy" && (
            <AcademyLauncher
              page={currentPage}
              onHelp={() => openHelp(currentPageKey)}
              onTour={() => startTour(currentPageKey)}
            />
          )}

          {helpPage && (
            <HelpDrawer
              page={helpPage}
              completed={Boolean(stored.completed[helpPage.key])}
              onClose={closeHelp}
              onStartTour={() => startTour(helpPage.key)}
              onMarkComplete={() =>
                markLessonComplete(helpPage.key, !stored.completed[helpPage.key])
              }
            />
          )}

          {tourPage && (
            <GuidedTour
              page={tourPage}
              stepIndex={tourStep}
              onStepChange={setTourStep}
              onClose={() => closeTour(true)}
              onComplete={completeTour}
            />
          )}

          {workflowState?.key === CRM_FULL_CYCLE_WORKFLOW.key && (
            <WorkflowTutorial
              workflow={CRM_FULL_CYCLE_WORKFLOW}
              sample={workflowState.sample}
              stepIndex={workflowState.stepIndex || 0}
              onStepChange={setWorkflowStep}
              onClose={closeWorkflow}
              onComplete={completeWorkflow}
              completeLabel="academy_finish_module"
            />
          )}

          {workflowState?.key === TASK_PLANNING_WORKFLOW.key && (
            <WorkflowTutorial
              workflow={TASK_PLANNING_WORKFLOW}
              sample={workflowState.sample}
              stepIndex={workflowState.stepIndex || 0}
              onStepChange={setWorkflowStep}
              onClose={closeWorkflow}
              onComplete={completeWorkflow}
              onWorkflowAction={handleWorkflowAction}
              actionLoading={workflowActionLoading}
              actionError={workflowActionError}
              completeLabel="academy_finish_module"
            />
          )}

          {workflowState?.key === TABLES_WORKFLOW.key && (
            <WorkflowTutorial
              workflow={TABLES_WORKFLOW}
              sample={workflowState.sample}
              stepIndex={workflowState.stepIndex || 0}
              onStepChange={setWorkflowStep}
              onClose={closeWorkflow}
              onComplete={completeWorkflow}
              completeLabel="academy_finish_training"
            />
          )}

          {workflowState?.key === MARKETING_GLOBAL_WORKFLOW.key && (
            <WorkflowTutorial
              workflow={MARKETING_GLOBAL_WORKFLOW}
              stepIndex={workflowState.stepIndex || 0}
              onStepChange={setWorkflowStep}
              onClose={closeWorkflow}
              onComplete={completeWorkflow}
              completeLabel="academy_finish_module"
            />
          )}

          {workflowState?.key === MARKETING_BIRTHDAY_WORKFLOW.key && (
            <WorkflowTutorial
              workflow={MARKETING_BIRTHDAY_WORKFLOW}
              stepIndex={workflowState.stepIndex || 0}
              onStepChange={setWorkflowStep}
              onClose={closeWorkflow}
              onComplete={completeWorkflow}
              completeLabel="academy_finish_module"
            />
          )}

          <WorkflowCleanupDialog
            mode={cleanupDialogMode}
            status={cleanupStatus}
            loading={cleanupLoading}
            error={cleanupError}
            onDeleteNow={deleteWorkflowDataNow}
            onKeepData={keepWorkflowData}
            onCloseNotice={closeCleanupNotice}
          />
        </>
      )}
    </AcademyContext.Provider>
  );
}

export function useAcademy() {
  const value = useContext(AcademyContext);
  if (!value) {
    throw new Error("useAcademy must be used inside AcademyProvider");
  }
  return value;
}
